import {
  SERVICE_WORKER_PATH,
  SERVICE_WORKER_SCOPE,
  getServiceWorkerRelease,
  getVersionedServiceWorkerUrl,
  isManagedServiceWorkerScriptUrl,
  shouldDeleteManagedCache,
} from './constants';

const ONE_SHOT_RELOAD_PREFIX = 'landing-sw-reload';

export const serviceWorkerTestHooks = {
  reloadPage() {
    window.location.reload();
  },
};

function getRootScopeUrl() {
  return new URL(SERVICE_WORKER_SCOPE, window.location.origin).href;
}

function getRegistrationScriptUrl(registration: ServiceWorkerRegistration) {
  return registration.active?.scriptURL ?? registration.waiting?.scriptURL ?? registration.installing?.scriptURL ?? null;
}

function getCurrentReloadMarkerKey() {
  return `${ONE_SHOT_RELOAD_PREFIX}:${getServiceWorkerRelease()}`;
}

function hasMatchingController(controller: ServiceWorker | null) {
  if (!controller) {
    return false;
  }

  const controllerUrl = new URL(controller.scriptURL);

  return controllerUrl.pathname === SERVICE_WORKER_PATH && controllerUrl.searchParams.get('release') === getServiceWorkerRelease();
}

function installOneShotReloadOnControllerChange() {
  let hasTriggered = false;

  const handleControllerChange = () => {
    if (hasTriggered || !hasMatchingController(navigator.serviceWorker.controller)) {
      return;
    }

    hasTriggered = true;

    try {
      const reloadMarkerKey = getCurrentReloadMarkerKey();

      if (window.sessionStorage.getItem(reloadMarkerKey) === 'true') {
        return;
      }

      window.sessionStorage.setItem(reloadMarkerKey, 'true');
    } catch {
      // Ignore session storage write failures.
    }

    serviceWorkerTestHooks.reloadPage();
  };

  navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

  return () => {
    navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  };
}

async function getExistingRegistrations() {
  const serviceWorkerContainer = navigator.serviceWorker as ServiceWorkerContainer & {
    getRegistrations?: () => Promise<ServiceWorkerRegistration[]>;
    getRegistration?: (clientURL?: string) => Promise<ServiceWorkerRegistration | undefined>;
  };

  if (typeof serviceWorkerContainer.getRegistrations === 'function') {
    return serviceWorkerContainer.getRegistrations();
  }

  if (typeof serviceWorkerContainer.getRegistration === 'function') {
    const registration = await serviceWorkerContainer.getRegistration();

    return registration ? [registration] : [];
  }

  return [];
}

async function cleanupLegacyRegistrations(desiredScriptUrl: string) {
  const rootScopeUrl = getRootScopeUrl();
  const desiredAbsoluteScriptUrl = new URL(desiredScriptUrl, window.location.origin).href;
  const registrations = await getExistingRegistrations();

  await Promise.all(
    registrations
      .filter((registration) => {
        if (registration.scope !== rootScopeUrl) {
          return false;
        }

        const registeredScriptUrl = getRegistrationScriptUrl(registration);

        if (!registeredScriptUrl) {
          return true;
        }

        if (!isManagedServiceWorkerScriptUrl(registeredScriptUrl)) {
          return true;
        }

        return registeredScriptUrl !== desiredAbsoluteScriptUrl;
      })
      .map((registration) => registration.unregister()),
  );
}

async function cleanupLegacyCaches() {
  if (typeof window === 'undefined' || typeof window.caches === 'undefined') {
    return;
  }

  const keepCacheNames = new Set<string>();
  const cacheNames = await window.caches.keys();

  await Promise.all(
    cacheNames
      .filter((cacheName) => shouldDeleteManagedCache(cacheName, keepCacheNames))
      .map((cacheName) => window.caches.delete(cacheName)),
  );
}

async function migrateAndRegisterCurrentServiceWorker() {
  if (navigator.onLine === false) {
    return;
  }

  const desiredScriptUrl = getVersionedServiceWorkerUrl();

  await cleanupLegacyRegistrations(desiredScriptUrl);
  await cleanupLegacyCaches();

  const registration = await navigator.serviceWorker.register(desiredScriptUrl, {
    scope: SERVICE_WORKER_SCOPE,
    updateViaCache: 'none',
  });

  await registration.update().catch(() => {
    // Ignore update checks that fail on transient network issues.
  });

  registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

export function registerLandingPageServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !import.meta.env.PROD) {
    return () => {};
  }

  const teardownControllerChangeListener = installOneShotReloadOnControllerChange();
  let isDisposed = false;

  void migrateAndRegisterCurrentServiceWorker().catch((error) => {
    if (!isDisposed) {
      console.error('Service worker registration failed:', error);
    }
  });

  return () => {
    isDisposed = true;
    teardownControllerChangeListener();
  };
}
